import {
    AfterViewInit,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    Input, OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {AbstractControl} from '@angular/forms';
import {NgForOfContext} from '@angular/common';
import {BaseModel} from '../depends/base.model';
import {TranslateService} from '../translate.service';
import {BehaviorSubject, Subject} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {IInfiniteScrollEvent} from "ngx-infinite-scroll";

const BATCH = 30;       // size of each query

@Component({
    selector: 'app-dynamic-resource-selector',
    templateUrl: './dynamic-resource-selector.component.html',
    styleUrls: ['./dynamic-resource-selector.component.css']
})
export class DynamicResourceSelectorComponent<T extends BaseModel> implements OnInit, AfterViewInit {

    @ViewChild('input', {static: true}) input: ElementRef;
    @ViewChild('resourceScroll', {static: false}) resourceScroll: ElementRef<HTMLElement>;
    @ContentChild('itemTemplate', {static: false}) itemTemplate: TemplateRef<NgForOfContext<T>>;

    // required inputs
    @Input() label: string;
    @Input() displayField: string = null;


    // optional inputs
    @Input() control: AbstractControl;
    @Input() secondDisplayField: string = null;  // caso o valor do display field seja null, mostra o valor do segundo field  (serve como uma segunda opÃ§Ã£o do que seÃ¡ visualizado)
    @Input() labelColor: string = '';
    @Input() customInputClass: string = '';
    @Input() removeSelectedMessage: string;
    @Input() customLabelClass : string = '';

    // default values
    @Input() batchSize: number = BATCH;
    @Input() allowCreation: boolean = false;
    @Input() maxHeight: number = 20 * this.batchSize > 250 ? 250 : 20 * this.batchSize;        // faÃ§o isso pois cada elemento tem mais ou menos 24px. A altura mÃ¡xima NÃO pode ser maior que a lista que desce, pois se nÃ£o tiver scroll nÃ£o carrega os outros items
    @Input() hideLabel: boolean = false;
    @Input() disabled?: boolean = false;
    @Input() customRenderer: boolean = false;
    @Input() position: string = 'bottom';
    @Input() canRemoveSelected: boolean = false;
    @Input() hideErrorIcon: boolean = false;


    @Output() onCreateButtonPressed: EventEmitter<void> = new EventEmitter<void>();
    @Output() onItemSelected: EventEmitter<T> = new EventEmitter<T>();
    @Output() onSelectionCancelled: EventEmitter<void> = new EventEmitter<void>();
    @Output() onDownloadResources: EventEmitter<{clear_resources: boolean, batch: number, page_index: number, search?: string}> = new EventEmitter<{clear_resources: boolean, batch: number, page_index: number, search?: string}>();
    @Output() onComponentReady: EventEmitter<{clear_resources: boolean, batch: number, page_index: number, search?: string}> = new EventEmitter<{clear_resources: boolean, batch: number, page_index: number, search?: string}>();

    private subject: Subject<string> = new Subject();

    public _resources = new BehaviorSubject<T[]>([]);
    public selectedResource: T = null;

    public searchInput: string;

    private touched: boolean = false;
    public isInvalid: boolean = false;

    public _displayList: string = 'none';
    public _loading: boolean = false;

    public totalPages: number;
    public pageIndex: number = 1;
    public finished: boolean = false;                                                                                   // boolean when end of database is rechead

    divElementFocused: boolean = false;
    inputElementFocused: boolean = false;

    constructor(
        public translate: TranslateService
    ) {
    }

    ngOnInit() {
    }

    ngAfterViewInit() {
        let params = this._buildParams();
        this.onComponentReady.emit(params);

        this.subject.pipe(
            debounceTime(800)
        ).subscribe(searchText => {
            this.searchInput = searchText;
            this._downloadResources(true);
        });
    }

    private _buildParams(clearResources: boolean = false) {
        if (clearResources)
            this.pageIndex = 1;

        let params = {
            clear_resources: clearResources,
            batch: BATCH,
            page_index: this.pageIndex
        };
        if (this.searchInput)
            params['search'] = this.searchInput;

        return params;
    }

    private _downloadResources(clearResources: boolean = false) {
        let params = this._buildParams(clearResources);
        this._loading = true;
        this.onDownloadResources.emit(params);
    }

    public addResources(newResources: T[], totalPages: number, clearResources: boolean = false) {
        this.pageIndex++;
        this.totalPages = totalPages;

        let currentResources = this._resources.getValue();
        if (clearResources) {
            this.resourceScroll.nativeElement.scrollTop = 0;
            this._resources.next(newResources);
        }
        else
            this._resources.next( currentResources.concat(newResources) );

        this._loading = false;
    }

    _onScroll(event: IInfiniteScrollEvent){
        if (this.totalPages && this.pageIndex > this.totalPages)
            return;
        this._downloadResources();
    }

    _onSearchInput(event) {
        let text = event.target.value;
        this.searchInput = text;
        this.subject.next(text);
    }

    _emitItemSelection(event, item:T) {
        this.onItemSelected.emit(item);
        this.setSelectedResource(item);
        this._checkNoRequiredField();
    }

    public setDirectResource(item: T) {
        this.selectedResource = item;
        this._resources.next([item]);

        if (this.control != null)
            this.control.setValue(item.id);

        let input = this.input.nativeElement;
        input.value = item[this.displayField];

        if (!item[this.displayField] && this.secondDisplayField)
            input.value = item[this.secondDisplayField];
    }

    public setSelectedResource(item: T | number) {
        if (typeof item == 'number') {
            let currentResources = this._resources.getValue();
            item = currentResources.find((resource: T) => resource.id == item);
        }
        else
            item = item as T;

        this.selectedResource = item;
        if (this.control != null)
            this.control.setValue(item.id);

        let input = this.input.nativeElement;
        input.value = item[this.displayField];

        if (!item[this.displayField] && this.secondDisplayField)
            input.value = item[this.secondDisplayField];
    }

    _emitNewItemButtonPressed(){
        this.onCreateButtonPressed.emit();
    }

    public isValid() : boolean {
        if (this.control == null)
            return true;

        let valid = !(this.control.invalid && this.touched);

        if(valid && this.control.validator && this.control.validator({} as AbstractControl).required ){
            if(this.touched && this.selectedResource == null) {
                valid = false;
            }
        }
        return valid;
    }

    _showResourceValue(resource: T){
        if (resource[this.displayField])
            return resource[this.displayField];

        if (this.secondDisplayField && resource[this.secondDisplayField])
            return resource[this.secondDisplayField];

        return ''
    }

    public cancelSelection() {
        this.selectedResource = null;
        if(this.control != null)
            this.control.patchValue(null);
        this.input.nativeElement.value = null;
        this.onSelectionCancelled.emit();
    }


    public showList(element: string) {
        if (!this.disabled) {
            if (element == 'div')
                this.divElementFocused = true;
            else if (element == 'input')
                this.inputElementFocused = true;

            this._displayList = 'flex';
        }
    }

    _onBlurSelector(element: string) {
        if (element == 'div')
            this.divElementFocused = false;
        else if (element == 'input')
            this.inputElementFocused = false;

        setTimeout(() => {
            if (!this.divElementFocused && !this.inputElementFocused) {
                this._displayList = 'none';
                this.touched = true;
                this._checkNoRequiredField();
            }
        },200);
    }

    private _checkNoRequiredField() {
        this.isInvalid = this.input.nativeElement.value.length > 0 && this.selectedResource == null;
    }

    public setLoading(value: boolean) {
        this._loading = value;
    }
}
